/**
 * The bridge between the Console's store and React.
 *
 * `Store` predates this migration and is kept deliberately. It owns the parts
 * that were hard to get right and are easy to get wrong again: the projection
 * stream's cursor, the resume decision that refuses to animate across a gap it
 * did not observe (ADR-107), and the per-role live transcripts assembled from
 * `attempt.output` frames (ADR-114). None of that is React's business, and
 * rewriting it as hooks would have risked all of it for no gain.
 *
 * `useSyncExternalStore` is the supported way to read from a store React does
 * not own. It matters here rather than being a formality: frames arrive several
 * times a second, and the alternative — subscribing in an effect and calling
 * `setState` — tears during concurrent rendering, showing one component a
 * newer scene than its sibling.
 */

import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { readConfig, readTheme, Store, type AppState } from '../store.js';

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({ children, store }: { children: ReactNode; store?: Store }): ReactNode {
  // Built once. A store rebuilt on re-render would reopen the event stream on
  // every render, which is both a leak and a reset of everything it has seen.
  const value = useMemo(() => store ?? new Store(readConfig()), [store]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (store === null) throw new Error('useStore was called outside a StoreProvider');
  return store;
}

/**
 * Reads one slice of store state.
 *
 * The selector's result must be referentially stable for unchanged state — the
 * store emits whole immutable snapshots, so selecting a field is safe and
 * selecting a freshly-built object is an infinite render loop.
 */
export function useStoreState<T>(select: (state: AppState) => T): T {
  const store = useStore();
  return useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => select(store.state),
    () => select(store.state),
  );
}

export function useConsoleConfig(): ReturnType<typeof readConfig> {
  return useStore().config;
}

export { readConfig, readTheme };
